import { useUserStore } from '~/stores/User'
import { usePositionStore } from '~/stores/Position'
import { useAccountStore } from '~/stores/Account'
import { useOrderStore } from '~/stores/Order'
import { useTradeHistoryStore } from '~/stores/TradeHistory'
import { useCurrentUserQuery } from '~/queries/currentUser'

export default defineNuxtPlugin(async (nuxtApp) => {
  const userStore = useUserStore()
  const accountStore = useAccountStore()
  const orderStore = useOrderStore()
  const positionStore = usePositionStore()
  const tradeHistoryStore = useTradeHistoryStore()
  const orderBookStore = useOrderBookStore()
  const currentUserQuery = useCurrentUserQuery()
  const bestBidOfferStore = useBestBidOfferStore()

  let initialized = false
  let initPromise: Promise<boolean> | null = null

  /**
   * Меняется при dispose.
   * Нужен, чтобы остановить старый init, если пользователь разлогинился
   * во время fetch/subscribe.
   */
  let lifecycleVersion = 0

  function isNotInitializedError(e: unknown) {
    return e instanceof Error && /not initialized/i.test(e.message)
  }

  async function safeRun(label: string, callback: () => unknown | Promise<unknown>) {
    try {
      await callback()
    } catch (e) {
      /**
       * unsubscribe вызван, когда подписки ещё не было.
       */
      if (isNotInitializedError(e)) {
        return
      }

      console.warn(`[trading-stores] ${label} failed`, e)
    }
  }

  function isActualInit(version: number) {
    return Boolean(userStore.isAuthenticated && userStore.user) && version === lifecycleVersion
  }

  async function loadCurrentUser() {
    try {
      const { data: user } = await currentUserQuery.refetch(true)
      if (!user) throw new Error('Current user is missing')

      userStore.$patch({ user, isAuthenticated: true })
    } catch (e) {
      userStore.reset()
      console.warn('[trading-stores] Failed to get current user', e)
      throw e
    }
  }

  async function resetTradingStores() {
    await Promise.all([
      safeRun('accountStore.reset', () => accountStore.reset?.()),
      safeRun('orderStore.reset', () => orderStore.reset?.()),
      safeRun('positionStore.reset', () => positionStore.reset?.()),
      safeRun('tradeHistoryStore.reset', () => tradeHistoryStore.reset?.()),
      safeRun('orderBookStore.resetAllOrderBooks', () => orderBookStore.resetAllOrderBooks?.()),
      safeRun('bestBidOfferStore.resetAllBestBidOffers', () =>
        bestBidOfferStore.resetAllBestBidOffers?.()
      )
    ])
  }

  async function unsubscribeTradingStores() {
    await Promise.all([
      safeRun('accountStore.unsubscribe', () => accountStore.unsubscribe()),
      safeRun('orderStore.unsubscribe', () => orderStore.unsubscribe()),
      safeRun('positionStore.unsubscribe', () => positionStore.unsubscribe?.()),
      safeRun('tradeHistoryStore.unsubscribe', () => tradeHistoryStore.unsubscribe?.()),
      safeRun('orderBookStore.releaseAllOrderBooks', () => orderBookStore.releaseAllOrderBooks?.()),
      safeRun('bestBidOfferStore.unsubscribeFromAllSymbols', () => bestBidOfferStore.releaseAll?.())
    ])
  }

  async function disposeTradingStores() {
    lifecycleVersion++

    const shouldUnsubscribe = initialized || Boolean(initPromise)

    initialized = false
    initPromise = null

    /**
     * на странице логина при первой загрузке initialized=false и initPromise=null,
     * поэтому unsubscribe вообще не вызываем.
     */
    if (shouldUnsubscribe) {
      await unsubscribeTradingStores()
    }

    await resetTradingStores()
  }

  /**
   * Прогоняет init-последовательность, проверяя isActualInit перед каждым шагом.
   *
   * fetch-шаги при отмене просто выходят — подписок ещё нет.
   * subscribe-шаги при отмене вызывают unsubscribe, т.к. часть подписок
   * уже могла быть создана.
   *
   * Возвращает true, только если последовательность дошла до конца
   * и подписки актуальны.
   */
  async function runInitSequence(version: number): Promise<boolean> {
    const fetchSteps = [
      () => accountStore.fetchAccount({ force: true }),
      () => accountStore.getCurrentAccountData(),
      () =>
        Promise.all([
          positionStore.fetchAllPosition(accountStore.currentAccount?.id ?? null, {
            force: true
          }),
          orderStore.fetchAllOrders()
        ])
    ]

    const subscribeSteps = [
      () => positionStore.subscribeWs(),
      () => accountStore.subscribeWs(),
      () => orderStore.subscribeToOrders(),
      () => tradeHistoryStore.subscribeWs()
    ]

    for (const step of fetchSteps) {
      if (!isActualInit(version)) return false
      await step()
    }

    for (const step of subscribeSteps) {
      if (!isActualInit(version)) {
        await unsubscribeTradingStores()
        return false
      }

      await step()
    }

    if (!isActualInit(version)) {
      await unsubscribeTradingStores()
      return false
    }

    return true
  }

  async function initTradingStores(): Promise<void> {
    if (initialized) return

    if (initPromise) {
      await initPromise
      return
    }

    const currentInitPromise = runInitSequence(lifecycleVersion)

    initPromise = currentInitPromise

    try {
      initialized = await currentInitPromise
    } catch (e) {
      initialized = false

      await unsubscribeTradingStores()
      await resetTradingStores()

      console.error('[trading-stores] Failed to init trading stores', e)
    } finally {
      if (initPromise === currentInitPromise) {
        initPromise = null
      }
    }
  }

  await loadCurrentUser().catch(() => undefined)

  watch(
    () => Boolean(userStore.isAuthenticated && userStore.user),
    async (isReady, wasReady) => {
      if (isReady) {
        await nextTick()
        await initTradingStores()
        return
      }

      /**
       * При первой загрузке страницы логина:
       * isAuthenticated=false
       * wasReady=undefined
       * initialized=false
       * initPromise=null
       *
       * В этом случае не надо делать unsubscribe.
       */
      if (wasReady || initialized || initPromise) {
        await disposeTradingStores()
        return
      }

      await resetTradingStores()
    },
    {
      immediate: true
    }
  )

  /**
   * Hooks обновляют user/auth-state.
   * Сам lifecycle trading stores управляется watcher'ом выше.
   * Так мы не получаем двойной dispose/init.
   */
  nuxtApp.hook('auth:logout', () => {
    userStore.reset()
  })

  nuxtApp.hook('auth:login', loadCurrentUser)
})
